import { JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationPersonalDetails';
import { INVESTMENT_JOIN_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/useInvestmentJoinAndAllocationController';
import { INVESTMENT_MANAGE_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentManage/InvestmentManage';
import { REJECTED_FILES_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentManage/RejectedFiles/RejectedFiles';
import { VALUATIONS_DRAWER_TYPE } from '@dealstack/frontend/investor/pages/Valuations/useValuationsController';
import {
	INVESTOR_WORKFLOW,
	resolveInvestorWorkflow,
} from '@dealstack/frontend/investor/pages/Valuations/Valuations.utils';
import {
	getInvestorInvestmentsWithSDWDocumentSummaries,
	getSingleInvestorInvestmentWithSDWDocumentSummary,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { SHAREHOLDER_INVESTMENT_STATUS } from '@dealstack/shared/lib/common/business/shareholder';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import {
	DataBankAccountTct,
	GetShareholderInvestmentEntryRes,
	GetShareholderInvestmentEntryWithSDWDocumentRes,
	GetShareholderSDWDocumentRes,
	InvestmentWorkflowCompletionStep,
	KYCWorkflowStatus,
	StandaloneKycWorkflowRes,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import { isAfter } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ISecurities } from './useActionPanelController.types';
import { BankingDetailsComponentProps } from './ValuationsDrawer/BankingDetailsComponent.types';
import { INVESTMENT_LEAVE_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentLeave/InvestmentLeave';

interface IActionPanelData {
	link?: string;
	showBank: boolean;
	hasWorkflow: boolean;
	status: SHAREHOLDER_INVESTMENT_STATUS;
	companyName?: string | null;
	security?: ISecurities[];
	createdAt: string;
	updatedAt: string;
	isSDWDocument: boolean;
	investorWorkflow: INVESTOR_WORKFLOW;
	bankDetails?: DataBankAccountTct;
	investorId: string;
	dealId?: string;
	mipId?: string;
	isStandaloneKyc?: boolean;
	kycWorkflowId?: string;
	linkText?: string;
}

type StandaloneKycData = StandaloneKycWorkflowRes;

export const useActionPanelController = (hasAllInvestmentsOption: boolean) => {
	const { investorId } = useParams();

	const [selectedActionCardInvestorId, setSelectedActionCardInvestorId] = useState<string | null>(
		null
	);
	const [actionPanelData, setActionPanelData] = useState<IActionPanelData[]>([]);

	const panelRef = useRef<HTMLDivElement>(null);

	const getInvestmentAndSDWSummaryRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SHAREHOLDER_INVESTMENT_WITH_SDW_DOCUMENTS_INVESTORID_GET, {
				investorId: investorId ?? '',
			}),
		],
		queryFn: ({ signal }) =>
			getSingleInvestorInvestmentWithSDWDocumentSummary(
				formatUnicorn(API_PATHS.SHAREHOLDER_INVESTMENT_WITH_SDW_DOCUMENTS_INVESTORID_GET, {
					investorId: investorId ?? '',
				}),
				signal
			),
		enabled: !hasAllInvestmentsOption && !!investorId,
	});

	const getInvestmentsAndSDWSummariesRequest = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_INVESTMENT_WITH_SDW_DOCUMENTS_GET],
		queryFn: ({ signal }) =>
			getInvestorInvestmentsWithSDWDocumentSummaries(
				API_PATHS.SHAREHOLDER_INVESTMENT_WITH_SDW_DOCUMENTS_GET,
				signal
			),
		enabled: hasAllInvestmentsOption,
	});

	const investmentsAndSDWSummariesPayload = useMemo(
		() => getInvestmentsAndSDWSummariesRequest.data?.payload ?? [],
		[getInvestmentsAndSDWSummariesRequest.data?.payload]
	);

	const getStandaloneKycSummary = useCallback(
		(standaloneKyc?: StandaloneKycData | null): IActionPanelData | undefined => {
			if (!standaloneKyc) {
				return;
			}
			const isInProgress = standaloneKyc.status === KYCWorkflowStatus.IN_PROGRESS;
			return {
				investorWorkflow: INVESTOR_WORKFLOW.KYC,
				showBank: false,
				hasWorkflow: true,
				status: SHAREHOLDER_INVESTMENT_STATUS.NEEDS_ATTENTION,
				companyName: standaloneKyc.companyName,
				createdAt: standaloneKyc.updatedAt,
				updatedAt: standaloneKyc.updatedAt,
				isSDWDocument: false,
				investorId: standaloneKyc.investorId,
				isStandaloneKyc: true,
				kycWorkflowId: standaloneKyc.kycWorkflowId,
				linkText: isInProgress ? 'valuations.action_panel.continue_workflow' : undefined,
				link: `/deal/${standaloneKyc.dealId}/mip/${standaloneKyc.mipId}/kyc/${standaloneKyc.kycWorkflowId}`,
			};
		},
		[]
	);

	const mappedActionPanelData = useCallback(
		async (
			data?: GetShareholderInvestmentEntryWithSDWDocumentRes & {
				standaloneKyc?: StandaloneKycData | null;
			}
		) => {
			if (!data) {
				return;
			}
			const investmentSummary = await getInvestmentSummary(data.investment);

			const sdwSummary = getSDWSummary(data.document, data.investment);

			const standaloneKycSummary = getStandaloneKycSummary(data.standaloneKyc);

			const response: IActionPanelData[] = [];

			if (!investmentSummary && !sdwSummary && !standaloneKycSummary) {
				return response;
			}

			if (standaloneKycSummary) {
				response.push(standaloneKycSummary);
			}

			if (investmentSummary && !sdwSummary) {
				response.push(investmentSummary);
			} else if (!investmentSummary && sdwSummary) {
				response.push(sdwSummary);
			} else if (investmentSummary && sdwSummary) {
				const investmentUpdatedAt = new Date(investmentSummary.updatedAt);
				const sdwSummaryUpdatedAt = new Date(sdwSummary.updatedAt);
				const isInvestmentUpdatedAfterSDW = isAfter(
					investmentUpdatedAt,
					sdwSummaryUpdatedAt
				);

				if (isInvestmentUpdatedAfterSDW) {
					response.push(investmentSummary, sdwSummary);
				} else {
					response.push(sdwSummary, investmentSummary);
				}
			}

			return response;
		},
		[getStandaloneKycSummary]
	);

	const sort = useCallback((finalData: IActionPanelData[][]) => {
		return finalData.sort((previousArray, nextArray) => {
			const previousItem = previousArray[0];
			const nextItem = nextArray[0];

			if (!previousItem && !nextItem) {
				return 0;
			}
			if (!previousItem) {
				return 1;
			}
			if (!nextItem) {
				return -1;
			}

			const previousDate = new Date(previousItem.updatedAt);
			const nextDate = new Date(nextItem.updatedAt);

			return isAfter(previousDate, nextDate) ? -1 : 1;
		});
	}, []);

	useEffect(() => {
		let cancelled = false;

		const processData = async () => {
			if (!hasAllInvestmentsOption) {
				const mappedData = await mappedActionPanelData(
					getInvestmentAndSDWSummaryRequest.data?.payload
				);
				if (!cancelled) {
					setActionPanelData(mappedData ?? []);
				}
				return;
			}

			const finalData: IActionPanelData[][] = [];

			for (const summary of investmentsAndSDWSummariesPayload) {
				const mappedData = await mappedActionPanelData(summary);
				if (mappedData) {
					finalData.push(mappedData);
				}
			}

			if (cancelled) {
				return;
			}

			if (finalData.length === 0) {
				setActionPanelData([]);
				return;
			}

			const result = sort(finalData).flat();

			setActionPanelData(result);
		};

		void processData();

		return () => {
			cancelled = true;
		};
	}, [
		hasAllInvestmentsOption,
		getInvestmentAndSDWSummaryRequest.data?.payload,
		investmentsAndSDWSummariesPayload,
	]);

	const [currentOpenedDrawerType, setCurrentOpenedDrawerType] =
		useState<VALUATIONS_DRAWER_TYPE | null>(null);

	const whatDoINeedInstruction =
		'/context/investor/valuations-page/checklist-of-required-documents.pdf';

	const whatDoINeedInstructionFileName = 'Checklist of required documents.pdf';

	const onDrawerClose = useCallback(() => {
		setSelectedActionCardInvestorId(null);
		setCurrentOpenedDrawerType(null);
	}, []);

	const mapSecurities = useCallback(
		(investment: GetShareholderInvestmentEntryRes): ISecurities[] => {
			const mapped: ISecurities[] = [];
			const isInvestmentCompleted =
				investment.investmentStatus === SHAREHOLDER_INVESTMENT_STATUS.COMPLETED &&
				investment.completionStep === InvestmentWorkflowCompletionStep.COMPLETED;
			if (isInvestmentCompleted) {
				for (const security of investment.holdings) {
					mapped.push({
						unit: security.totalNumber,
						name: security.name,
						cost: security.totalCost,
						issuedIn: security.issuedIn,
					});
				}
			} else {
				for (const security of investment.securities) {
					mapped.push({
						unit: security.totalNumber,
						name: security.name,
						securityType: security.type,
						cost: security.totalCosts?.totalCost ?? 0,
						currency: security.currency,
						issuedIn: security.issuedIn,
					});
				}
			}
			return mapped;
		},
		[]
	);

	const getInvestmentSummary = useCallback(
		async (investment?: GetShareholderInvestmentEntryRes) => {
			if (!investment) {
				return;
			}

			const isNewOffer =
				investment.investmentStatus === SHAREHOLDER_INVESTMENT_STATUS.NEW_OFFER;
			const isNeedAttention =
				investment.investmentStatus === SHAREHOLDER_INVESTMENT_STATUS.NEEDS_ATTENTION;
			const areMorePivDocsRequested = !!investment.areMorePivDocsRequested;

			const investorWorkflow = resolveInvestorWorkflow(investment);

			const isLeaverTsaNotCompleted =
				investment.isLeaverInvestmentWorkflow && investment.completionStep !== 'COMPLETED';
			const isJoinerTsaNotCompleted =
				!investment.isLeaverInvestmentWorkflow && investment.completionStep !== 'COMPLETED';

			const isOnContracts = investment.completionStep === 'CONTRACTS';

			let link = `/investment/${investment.dealId}/${investment.mipId}/${investment.investmentWorkflowId}/`;

			const personalInformationPath = `${INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.PERSONAL_INFORMATION}`;

			if (isOnContracts && isJoinerTsaNotCompleted) {
				link += `join${INVESTMENT_JOIN_ROUTES.CONTRACTS}`;
			} else if (isNewOffer || isJoinerTsaNotCompleted) {
				link += `join${personalInformationPath}`;
			} else if (isLeaverTsaNotCompleted) {
				link += `leave${personalInformationPath}`;
			} else if (areMorePivDocsRequested) {
				link += `manage${INVESTMENT_MANAGE_ROUTES.MORE_DOCS_REQUESTED}${REJECTED_FILES_ROUTES.COMPANY}`;
			} else if (isNeedAttention && investment.hasOutstandingKyc) {
				// KYC needs investor action (rejected / missing item). KYC takes
				// priority over outstanding signatures because signatures aren't
				// accepted until KYC is approved.
				link += `manage${INVESTMENT_MANAGE_ROUTES.REJECTED_FILES}${REJECTED_FILES_ROUTES.PERSONAL}`;
			} else if (isNeedAttention && investment.hasOutstandingDocuments) {
				// Investor's join/leave workflow is complete but additional signatures
				// (PIV / witness / representative) are still pending — route to contracts
				// so they can sign for those roles.
				link += investment.isLeaverInvestmentWorkflow
					? `leave${INVESTMENT_LEAVE_ROUTES.CONTRACTS}`
					: `join${INVESTMENT_JOIN_ROUTES.CONTRACTS}`;
			} else {
				link += `manage${INVESTMENT_MANAGE_ROUTES.REJECTED_FILES}${REJECTED_FILES_ROUTES.PERSONAL}`;
			}

			const hasWorkflow = isNewOffer || isNeedAttention;

			const showBank =
				!hasWorkflow && !investment.isInvestorVoided && investment.showBankDetails;

			const data = {
				investorWorkflow,
				link,
				showBank,
				hasWorkflow,
				status: investment.investmentStatus,
				companyName: investment.entityName,
				security: investment.isInvestorVoided ? undefined : mapSecurities(investment),
				createdAt: investment.latestTransaction.createdAt,
				updatedAt: investment.updatedAt,
				isSDWDocument: false,
				investorId: investment.investorId,
				bankDetails: investment.bankDetails,
				dealId: investment.dealId,
				mipId: investment.mipId,
			};

			return data;
		},
		[getInvestmentAndSDWSummaryRequest.data?.payload]
	);

	const getSDWSummary = useCallback(
		(
			document?: GetShareholderSDWDocumentRes,
			investment?: GetShareholderInvestmentEntryRes | null
		) => {
			if (!document) {
				return;
			}

			const status = document.isAllInvestorAndPIVSigned
				? SHAREHOLDER_INVESTMENT_STATUS.COMPLETED
				: SHAREHOLDER_INVESTMENT_STATUS.NEEDS_ATTENTION;

			return {
				investorWorkflow: INVESTOR_WORKFLOW.DOCUMENT,
				showBank: false,
				hasWorkflow: true,
				status,
				companyName: document.companyName,
				createdAt: document.createdAt,
				updatedAt: document.updatedAt,
				isSDWDocument: true,
				investorId: document.investorId,
				// Prefer the SDW document's own dealId/mipId so the click still navigates
				// when the underlying investment record fails to load on the backend.
				dealId: document.dealId ?? investment?.dealId,
				mipId: document.mipId ?? investment?.mipId,
			};
		},
		[getInvestmentsAndSDWSummariesRequest.data?.payload]
	);

	const navigate = useNavigate();

	const onNonSDWDocumentClick = useCallback((link?: string) => {
		if (!link) {
			return;
		}
		navigate(link);
	}, []);

	const onSDWDocumentClick = useCallback(
		(investorId: string, dealId?: string, mipId?: string) => {
			if (!dealId || !mipId) {
				return;
			}
			navigate(`/documents/deal/${dealId}/mip/${mipId}/investor/${investorId}`);
		},
		[navigate]
	);

	const bankingDetails = useMemo((): BankingDetailsComponentProps => {
		const bankAccount = actionPanelData.find(
			(data) => data.investorId === selectedActionCardInvestorId && !data.isSDWDocument
		)?.bankDetails;

		return {
			name: bankAccount?.accountHolder ?? '',
			bankName: bankAccount?.bankName ?? '',
			accNumber: bankAccount?.accountNumber ?? '',
			iban: bankAccount?.iban ?? '',
			swift: bankAccount?.swiftCode ?? '',
			identifiers: bankAccount?.identifiers ?? [],
			countryCode: bankAccount?.countryCode,
			currency: bankAccount?.currency,
			instructions: bankAccount?.investorInstructions || undefined,
		};
	}, [actionPanelData, selectedActionCardInvestorId]);

	return {
		setSelectedActionCardInvestorId,
		panelRef,
		actionPanelData,
		onNonSDWDocumentClick,
		onSDWDocumentClick,
		isLoading:
			getInvestmentsAndSDWSummariesRequest.isFetching ||
			getInvestmentAndSDWSummaryRequest.isFetching,
		currentDrawer: currentOpenedDrawerType,
		openDrawer: setCurrentOpenedDrawerType,
		bankingDetails,
		selectedActionCardInvestorId,
		onDrawerClose,
		whatDoINeedInstruction,
		whatDoINeedInstructionFileName,
	};
};
